import { AppPaths } from '@/config/app-paths'
import type { WorkspaceDesk } from '@/config/workspace-desk'
import {
  isCoordinatorPortal,
  isProDeskSuperAdmin,
  isThinAdmin,
  proEmployeeDirectoryNavAllowed,
  proExitWorkspaceAllowed,
  proOnboardingWorkspaceAllowed,
  staffCapabilitySet,
  userHasProDeskRole,
} from '@/shared/lib/staff-capabilities'

function proDeskLandingPath(roles: readonly string[], capabilities: readonly string[]): string {
  // Document filers on the PRO desk land on outstanding packs first.
  if (capabilities.includes('document_filing_submit')) {
    return AppPaths.documentFilingOutstanding
  }
  if (proOnboardingWorkspaceAllowed(capabilities, roles)) return AppPaths.proOnboardingHub
  if (userHasProDeskRole(roles) && proExitWorkspaceAllowed(capabilities, roles)) {
    return AppPaths.proExitHub
  }
  if (userHasProDeskRole(roles) && proEmployeeDirectoryNavAllowed(capabilities, roles)) {
    return AppPaths.workforceDirectory
  }
  if (isProDeskSuperAdmin(roles)) return AppPaths.proDeskAdmin
  return AppPaths.proOnboardingHub
}

function normalizePath(pathname: string): string {
  const base = pathname.split('?')[0]?.split('#')[0]?.trim() ?? '/'
  if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1)
  return base || '/'
}

function isThinAdminWorkforcePersonPath(path: string): boolean {
  if (path === AppPaths.workforceDirectory) return true
  const prefix = `${AppPaths.workforceDirectory}/employees/`
  if (!path.startsWith(prefix)) return false
  const rest = path.slice(prefix.length)
  return rest.length > 0 && !rest.includes('/')
}

function isStocksWorkspacePath(path: string): boolean {
  if (path === AppPaths.adminStocks || path.startsWith(`${AppPaths.adminStocks}/`)) return true
  const parts = path.split('/').filter(Boolean)
  return parts.length >= 3 && parts[0] === 't' && parts[2] === 'stocks'
}

/**
 * Roster duties on the supervisor desk: directory, deployment, consolidate
 * attendance, staff roster/card, and the client board. Payroll console,
 * economics, and other workforce tools stay off this host.
 */
function isSupervisorRosterDutyPath(path: string): boolean {
  if (path === AppPaths.workforceDirectory) return true
  if (
    path.startsWith('/workforce/employees/') &&
    path !== AppPaths.workforceAddEmployee
  ) {
    return true
  }
  if (
    path === AppPaths.workforceDeployment ||
    path.startsWith(`${AppPaths.workforceDeployment}/`)
  ) {
    return true
  }
  if (
    path === AppPaths.payrollDailyAttendance ||
    path.startsWith(`${AppPaths.payrollDailyAttendance}/`)
  ) {
    return true
  }
  if (
    path === AppPaths.payrollAdminStaff ||
    path.startsWith(`${AppPaths.payrollAdminStaff}/`)
  ) {
    return true
  }
  if (path === AppPaths.adminClients) return true
  if (!path.startsWith(`${AppPaths.adminClients}/`)) return false
  const rest = path.slice(`${AppPaths.adminClients}/`.length)
  return rest.length > 0 && !rest.includes('/')
}

/** Thin-admin landing — in-house attendance. Dashboard and payrolls stay parked. */
export const THIN_ADMIN_LANDING_PATH = AppPaths.inHouseAttendance

/**
 * Paths a plain Administrator (thin admin) may open.
 * Single source for the route guard and tests. Account self-service is always allowed.
 */
export function isThinAdminPathAllowed(pathname: string): boolean {
  const path = normalizePath(pathname)

  if (path.startsWith('/account')) return true
  if (path === AppPaths.home || path === '/') return true

  if (path === AppPaths.inHouseAttendance) return true
  if (path === AppPaths.bossAttendance) return true
  if (isThinAdminWorkforcePersonPath(path)) return true

  if (path === AppPaths.adminGatePasses || path.startsWith(`${AppPaths.adminGatePasses}/`)) {
    return true
  }

  if (path === AppPaths.adminStockApprovals) return true
  if (isStocksWorkspacePath(path)) return true

  if (path === AppPaths.adminTeam || path === AppPaths.adminTeamRoles) return true
  if (path.startsWith(`${AppPaths.adminTeam}/`)) return true

  if (path === AppPaths.documentFilingAssign) return true
  if (path === AppPaths.documentFilingReview || path.startsWith(`${AppPaths.documentFilingReview}/`)) {
    return true
  }

  if (path === AppPaths.adminDesktopActivity) return true

  return false
}

export function isDocumentFilingOnlyPathAllowed(pathname: string): boolean {
  const path = normalizePath(pathname)
  if (path.startsWith('/account')) return true
  if (path === AppPaths.home || path === '/') return true
  if (path === AppPaths.documentFilingOutstanding || path.startsWith(`${AppPaths.documentFilingOutstanding}/`)) {
    return true
  }
  return false
}
export function defaultLandingPathForDesk(
  desk: WorkspaceDesk | null,
  roles: readonly string[] = [],
  capabilities: readonly string[] = [],
): string {
  // HR host keeps the HR dashboard. The admin shell stays on admin.*.
  if (isThinAdmin(roles) && desk !== 'hr') {
    return THIN_ADMIN_LANDING_PATH
  }

  switch (desk) {
    case 'pro':
      return proDeskLandingPath(roles, capabilities)
    case 'onboarding':
      return AppPaths.hrOnboardingHub
    case 'hr':
      return capabilities.includes('document_filing_submit')
        ? AppPaths.documentFilingOutstanding
        : AppPaths.workforceDirectory
    case 'recruiter':
      return AppPaths.recruitmentJobs
    case 'reception':
      return AppPaths.hrWalkInIntake
    case 'sales':
      return AppPaths.salesDashboard
    case 'client':
      return AppPaths.clientApplicants
    case 'supervisor':
      return isCoordinatorPortal(staffCapabilitySet(capabilities))
        ? AppPaths.payrollCoordinatorAttendance
        : AppPaths.payrollTeamAttendance
    case 'finance':
      return AppPaths.payrollAccounts
    case 'app':
      return AppPaths.home
    case 'admin':
      return AppPaths.home
    case 'operations':
      return AppPaths.home
    default:
      return AppPaths.home
  }
}

/** Sidebar subtitle under the AM Global logo on desk subdomains. */
export function workspaceDeskSubtitle(desk: WorkspaceDesk | null): string | null {
  switch (desk) {
    case 'pro':
      return 'PRO workspace'
    case 'onboarding':
      return 'Onboarding'
    case 'recruiter':
      return 'Recruitment'
    case 'reception':
      return 'Reception'
    case 'hr':
      return 'HR & onboarding'
    case 'finance':
      return 'Finance'
    case 'sales':
      return 'Sales'
    case 'client':
      return 'Client portal'
    case 'supervisor':
      return 'Supervisor'
    case 'app':
      return 'Multi-role workspace'
    default:
      return null
  }
}

/**
 * Whether [pathname] may render on this desk host (mirrors Flutter `WebPortalConfig.isPathAllowed`).
 * Login is handled outside the shell; account self-service is always allowed on desk hosts.
 */
export function isPathAllowedOnDesk(desk: WorkspaceDesk, pathname: string): boolean {
  const path = normalizePath(pathname)

  if (path.startsWith('/account')) return true
  if (path === AppPaths.supervisorMaterialRequest) return true

  switch (desk) {
    case 'pro':
      return (
        path === AppPaths.home ||
        path === AppPaths.proOnboardingHub ||
        path.startsWith(`${AppPaths.proOnboardingHub}/`) ||
        path === AppPaths.proExitHub ||
        path.startsWith(`${AppPaths.proExitHub}/`) ||
        path === AppPaths.proDeskAdmin ||
        path.startsWith('/workforce') ||
        path === AppPaths.documentFilingOutstanding ||
        path.startsWith(`${AppPaths.documentFilingOutstanding}/`)
      )
    case 'onboarding':
      return (
        path === AppPaths.home ||
        path.startsWith('/hr/onboarding') ||
        path === AppPaths.hrInterviewApplications ||
        path.startsWith(`${AppPaths.hrInterviewApplications}/`) ||
        (path.startsWith('/workforce') &&
          path !== AppPaths.workforceCompleteness &&
          !path.startsWith(`${AppPaths.workforceCompleteness}/`)) ||
        path === AppPaths.hrGatePasses ||
        path.startsWith(`${AppPaths.hrGatePasses}/`)
      )
    case 'recruiter':
      return (
        path === AppPaths.home ||
        path.startsWith('/recruitment') ||
        path === AppPaths.recruitmentJobWizard ||
        path === AppPaths.adminLeaves ||
        path.startsWith(`${AppPaths.adminLeaves}/`) ||
        path === AppPaths.payrollTeamAttendance ||
        path.startsWith(`${AppPaths.payrollTeamAttendance}/`) ||
        path === AppPaths.payrollAttendanceIssues ||
        path.startsWith(`${AppPaths.payrollAttendanceIssues}/`)
      )
    case 'reception':
      return (
        path === AppPaths.home ||
        path.startsWith('/recruitment/talent-pool') ||
        path === AppPaths.hrWalkInIntake
      )
    case 'hr':
      return (
        path === AppPaths.home ||
        path.startsWith('/recruitment/talent-pool') ||
        path === AppPaths.hrWalkInIntake ||
        path === AppPaths.hrOnboardingFollowUp ||
        path.startsWith('/hr/onboarding/cases/') ||
        path.startsWith('/hr/onboarding/joining-form/') ||
        path.startsWith('/hr/onboarding/pro-handover/') ||
        path.startsWith('/workforce') ||
        // Attendance disputes / issues: HR sits between employees and supervisors.
        path === AppPaths.payrollAttendanceIssues ||
        path.startsWith(`${AppPaths.payrollAttendanceIssues}/`) ||
        path === AppPaths.payrollSelfAttendance ||
        path.startsWith(`${AppPaths.payrollSelfAttendance}/`) ||
        path === AppPaths.payrollTeamAttendance ||
        path.startsWith(`${AppPaths.payrollTeamAttendance}/`) ||
        path === AppPaths.payrollCoordinatorTeam ||
        path === AppPaths.payrollAttendanceNotifications ||
        path.startsWith(`${AppPaths.payrollAttendanceNotifications}/`) ||
        path === AppPaths.adminLeaves ||
        path.startsWith(`${AppPaths.adminLeaves}/`) ||
        path === AppPaths.hrGatePasses ||
        path.startsWith(`${AppPaths.hrGatePasses}/`) ||
        path === AppPaths.hrInterviewApplications ||
        path.startsWith(`${AppPaths.hrInterviewApplications}/`) ||
        path === AppPaths.documentFilingOutstanding ||
        path.startsWith(`${AppPaths.documentFilingOutstanding}/`)
      )
    case 'sales':
      return (
        path === AppPaths.home ||
        path === AppPaths.salesDashboard ||
        path.startsWith('/sales/') ||
        path === AppPaths.adminClients ||
        path.startsWith(`${AppPaths.adminClients}/`) ||
        path === AppPaths.adminAnalytics ||
        path === AppPaths.adminRecruitmentSearch ||
        path === AppPaths.adminNotificationRules ||
        path === AppPaths.adminContactInquiries ||
        path.startsWith(`${AppPaths.adminContactInquiries}/`)
      )
    case 'client':
      return path === AppPaths.home || path.startsWith('/client')
    case 'supervisor':
      return (
        path === AppPaths.home ||
        path === AppPaths.payrollTeamAttendance ||
        path.startsWith(`${AppPaths.payrollTeamAttendance}/`) ||
        path === AppPaths.payrollAvailablePool ||
        path === AppPaths.payrollCoordinatorAttendance ||
        path === AppPaths.payrollCoordinatorTeam ||
        path === AppPaths.supervisorMobileApp ||
        path === AppPaths.payrollAttendanceIssues ||
        path.startsWith(`${AppPaths.payrollAttendanceIssues}/`) ||
        path === AppPaths.supervisorBulkHiring ||
        path.startsWith(`${AppPaths.supervisorBulkHiring}/`) ||
        path === AppPaths.hiringRequisitions ||
        path.startsWith(`${AppPaths.hiringRequisitions}/`) ||
        path.startsWith('/hiring-requisitions/') ||
        path === AppPaths.supervisorStaffDocs ||
        path.startsWith(`${AppPaths.supervisorStaffDocs}/`) ||
        path === AppPaths.supervisorMaterialRequest ||
        path === AppPaths.supervisorGatePass ||
        path === AppPaths.adminGatePasses ||
        path.startsWith(`${AppPaths.adminGatePasses}/`) ||
        path === AppPaths.myDesktopActivity ||
        isStocksWorkspacePath(path) ||
        isSupervisorRosterDutyPath(path)
      )
    case 'finance':
      // Monthly close + people/clients + attendance review + operate runs + finance/invoicing.
      return (
        path === AppPaths.home ||
        path === AppPaths.payrollAccounts ||
        path.startsWith(`${AppPaths.payrollAccounts}/`) ||
        path === AppPaths.payrollDailyAttendance ||
        path.startsWith(`${AppPaths.payrollDailyAttendance}/`) ||
        path === AppPaths.payrollRuns ||
        path.startsWith(`${AppPaths.payrollRuns}/`) ||
        path.startsWith(`${AppPaths.payrollAdminRuns}/`) ||
        path.startsWith('/payroll/admin/periods/') ||
        path.startsWith('/payroll/admin/pay-months/') ||
        path === AppPaths.payrollAdminSettlements ||
        path.startsWith(`${AppPaths.payrollAdminSettlements}/`) ||
        path === AppPaths.financeOverview ||
        path.startsWith(`${AppPaths.financeOverview}/`) ||
        path === AppPaths.invoicing ||
        path.startsWith(`${AppPaths.invoicing}/`) ||
        path === AppPaths.accountsContracts ||
        path.startsWith(`${AppPaths.accountsContracts}/`)
      )
    case 'app':
      return true
    default:
      return true
  }
}

/** Safe internal return URL or desk landing when missing/invalid. */
export function resolvePostAuthPath(
  returnTo: string | null | undefined,
  host: string,
  resolveDesk: (host: string) => WorkspaceDesk | null,
  roles: readonly string[] = [],
  capabilities: readonly string[] = [],
): string {
  const desk = resolveDesk(host)
  const thinAdmin = isThinAdmin(roles)
  const hrDesk = desk === 'hr'

  if (returnTo) {
    try {
      const decoded = decodeURIComponent(returnTo.trim())
      if (decoded.startsWith('/') && !decoded.startsWith('//')) {
        if (thinAdmin && !hrDesk && !isThinAdminPathAllowed(decoded)) {
          return THIN_ADMIN_LANDING_PATH
        }
        if ((!thinAdmin || hrDesk) && desk && !isPathAllowedOnDesk(desk, decoded)) {
          return defaultLandingPathForDesk(desk, roles, capabilities)
        }
        return decoded
      }
    } catch {
      // fall through to desk landing
    }
  }

  return defaultLandingPathForDesk(desk, roles, capabilities)
}
